import { useState, useEffect, useCallback } from "react";
import { API } from "../App";
import { toast } from "sonner";
import { BookOpen, ThumbsUp, ThumbsDown, Loader2, Search, Filter, CheckCircle, RefreshCw, Plus } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { ScrollArea } from "../components/ui/scroll-area";
import { Dialog } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import axios from "axios";
import Layout from "../components/Layout";
import { StatCard, FeedbackCard } from "../components/ai-training/FeedbackCards";
import { EditFeedbackDialog, AddFeedbackDialog, DeleteFeedbackDialog } from "../components/ai-training/FeedbackDialogs";


const AITrainingPage = () => {
  const [feedbackList, setFeedbackList] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState([]);
  
  // Filters
  const [ratingFilter, setRatingFilter] = useState("all");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  
  // Edit dialog
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingFeedback, setEditingFeedback] = useState(null);
  const [editForm, setEditForm] = useState({
    corrected_response: "",
    category: "",
    notes: ""
  });
  const [saving, setSaving] = useState(false);
  
  // Delete dialog
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletingFeedback, setDeletingFeedback] = useState(null);
  
  // Add new dialog
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [newFeedback, setNewFeedback] = useState({
    original_question: "",
    original_ai_response: "",
    corrected_response: "",
    category: undefined,
    rating: "negative"
  });

  const fetchData = useCallback(async () => {
    try {
      const [feedbackRes, statsRes] = await Promise.all([
        axios.get(`${API}/ai-training/feedback`, { 
          params: {
            rating: ratingFilter !== "all" ? ratingFilter : undefined,
            category: categoryFilter !== "all" ? categoryFilter : undefined,
            limit: 100
          },
          withCredentials: true 
        }),
        axios.get(`${API}/ai-training/stats`, { withCredentials: true })
      ]);
      
      setFeedbackList(feedbackRes.data.feedback || []);
      setCategories(feedbackRes.data.categories || []);
      setStats(statsRes.data);
    } catch (error) {
      toast.error("Failed to load training data");
    } finally {
      setLoading(false);
    }
  }, [ratingFilter, categoryFilter]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleEdit = (feedback) => {
    setEditingFeedback(feedback);
    setEditForm({
      corrected_response: feedback.corrected_response || "",
      category: feedback.category || undefined,
      notes: feedback.notes || ""
    });
    setEditDialogOpen(true);
  };

  const handleSaveEdit = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/ai-training/feedback/${editingFeedback.feedback_id}`, editForm, {
        withCredentials: true
      });
      toast.success("Feedback updated!");
      setEditDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error("Failed to update feedback");
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (feedback) => {
    try {
      await axios.put(`${API}/ai-training/feedback/${feedback.feedback_id}`, {
        is_active: !feedback.is_active
      }, { withCredentials: true });
      toast.success(feedback.is_active ? "Feedback disabled" : "Feedback enabled");
      fetchData();
    } catch (error) {
      toast.error("Failed to update feedback");
    }
  };

  const handleDelete = (feedback) => {
    setDeletingFeedback(feedback);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    try {
      await axios.delete(`${API}/ai-training/feedback/${deletingFeedback.feedback_id}`, {
        withCredentials: true
      });
      toast.success("Feedback deleted");
      setDeleteDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error("Failed to delete feedback");
    }
  };

  const handleAddNew = async () => {
    if (!newFeedback.original_question.trim() || !newFeedback.corrected_response.trim()) {
      toast.error("Please fill in the question and correct response");
      return;
    }
    
    setSaving(true);
    try {
      const payload = {
        original_question: newFeedback.original_question,
        original_ai_response: newFeedback.original_ai_response || "(No AI response - manual entry)",
        corrected_response: newFeedback.corrected_response,
        rating: newFeedback.rating
      };
      // Only include category if it has a value
      if (newFeedback.category) {
        payload.category = newFeedback.category;
      }
      
      await axios.post(`${API}/ai-training/feedback`, payload, { withCredentials: true });
      toast.success("Training data added!");
      setAddDialogOpen(false);
      setNewFeedback({
        original_question: "",
        original_ai_response: "",
        corrected_response: "",
        category: undefined,
        rating: "negative"
      });
      fetchData();
    } catch (error) {
      toast.error("Failed to add training data");
    } finally {
      setSaving(false);
    }
  };

  // Filter feedback by search query
  const filteredFeedback = feedbackList.filter(fb => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      fb.original_question?.toLowerCase().includes(query) ||
      fb.original_ai_response?.toLowerCase().includes(query) ||
      fb.corrected_response?.toLowerCase().includes(query)
    );
  });

  if (loading) {
    return (
      <Layout title="AI Training">
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout title="AI Training">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center shadow-lg shadow-violet-500/25">
              <BookOpen className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-slate-900">AI Training Center</h1>
              <p className="text-sm text-slate-500">Manage corrections and improve AI responses</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={fetchData}
              className="gap-2"
            >
              <RefreshCw className="w-4 h-4" />
              Refresh
            </Button>
            <Button
              onClick={() => setAddDialogOpen(true)}
              className="gap-2 bg-violet-600 hover:bg-violet-700"
              data-testid="add-training-btn"
            >
              <Plus className="w-4 h-4" />
              Add Training Data
            </Button>
          </div>
        </div>

        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <StatCard
              icon={BookOpen}
              label="Total Feedback"
              value={stats.total}
              color="bg-slate-600"
            />
            <StatCard
              icon={ThumbsUp}
              label="Good Responses"
              value={stats.positive}
              color="bg-emerald-500"
            />
            <StatCard
              icon={ThumbsDown}
              label="Needs Correction"
              value={stats.negative}
              color="bg-red-500"
            />
            <StatCard
              icon={CheckCircle}
              label="With Corrections"
              value={stats.with_corrections}
              color="bg-violet-500"
            />
          </div>
        )}

        {/* Info Card */}
        <Card className="border-violet-200 bg-violet-50">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <BookOpen className="w-5 h-5 text-violet-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium text-violet-900">How AI Training Works</p>
                <p className="text-sm text-violet-700 mt-1">
                  When you mark a response as &quot;Needs Correction&quot; and provide the correct answer, the AI will 
                  automatically reference this correction when similar questions come up in the future. 
                  The more corrections you add, the smarter the AI becomes!
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[200px] max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Search questions or responses..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9"
            />
          </div>
          <Select value={ratingFilter} onValueChange={setRatingFilter}>
            <SelectTrigger className="w-[150px]">
              <Filter className="w-4 h-4 mr-2" />
              <SelectValue placeholder="Rating" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Ratings</SelectItem>
              <SelectItem value="positive">Good Only</SelectItem>
              <SelectItem value="negative">Needs Fix Only</SelectItem>
            </SelectContent>
          </Select>
          <Select value={categoryFilter} onValueChange={setCategoryFilter}>
            <SelectTrigger className="w-[150px]">
              <SelectValue placeholder="Category" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Categories</SelectItem>
              {categories.filter(Boolean).map(cat => (
                <SelectItem key={cat} value={cat}>{cat}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Feedback List */}
        <Card className="border-slate-200">
          <CardHeader>
            <CardTitle className="text-lg">Training Data ({filteredFeedback.length})</CardTitle>
            <CardDescription>
              Click on an item to expand and see full details
            </CardDescription>
          </CardHeader>
          <CardContent>
            {filteredFeedback.length === 0 ? (
              <div className="text-center py-12">
                <BookOpen className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-500">No training data yet</p>
                <p className="text-sm text-slate-400 mt-1">
                  Rate AI responses in the Webhook Monitor or add data manually
                </p>
              </div>
            ) : (
              <ScrollArea className="h-[500px]">
                <div className="space-y-3 pr-4">
                  {filteredFeedback.map((fb) => (
                    <FeedbackCard
                      key={fb.feedback_id}
                      feedback={fb}
                      onEdit={handleEdit}
                      onDelete={handleDelete}
                      onToggleActive={handleToggleActive}
                    />
                  ))}
                </div>
              </ScrollArea>
            )}
          </CardContent>
        </Card>

        {/* Edit Dialog */}
        <EditFeedbackDialog open={editDialogOpen} onOpenChange={setEditDialogOpen} editingFeedback={editingFeedback} editForm={editForm} setEditForm={setEditForm} onSave={handleSaveEdit} saving={saving} />

        {/* Add New Dialog */}
        <AddFeedbackDialog open={addDialogOpen} onOpenChange={setAddDialogOpen} newFeedback={newFeedback} setNewFeedback={setNewFeedback} onSave={handleAddNew} saving={saving} />

        {/* Delete Confirmation */}
        <DeleteFeedbackDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen} onConfirm={confirmDelete} />
      </div>
    </Layout>
  );
};

export default AITrainingPage;
